import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { MdInfo, MdWarning, MdRadar, MdOpenInNew, MdDangerous } from 'react-icons/md';
import { Box, Callout, Card, Flex, Heading, Icon, ProgressBar, Text } from 'core/components';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import { addCommas } from 'app/util/utils';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import { COMPANY_MPS_QUERY } from 'app/views/metrics/queries/index.js';
import FilteredDevicesLink from 'app/components/links/FilteredDevicesLink';
/**
 * Warning component displayed when device limits are reached or exceeded
 * Uses intent colors and built-in Callout styling
 */
const DeviceLimitWarning = ({ overageLimitReached, ...props }) => (
  <Callout intent={overageLimitReached ? 'danger' : 'warning'} fontWeight="normal" maxWidth={425} mt={1} {...props}>
    <Flex alignItems="center">
      {overageLimitReached ? (
        <Icon icon={MdDangerous} iconSize={20} mr="8px" />
      ) : (
        <Icon icon={BsFillExclamationTriangleFill} iconSize={15} mr="8px" />
      )}
      <Flex flexDirection="column">
        <Text>You have reached your device limit.</Text>
        <Text textDecoration="underline">Please contact your account team.</Text>
      </Flex>
    </Flex>
  </Callout>
);

@inject('$devices', '$metrics', '$setup')
@observer
class NmsDeviceUsageCard extends Component {
  render() {
    const {
      name: planName,
      description: planDescription,
      full_retention,
      device_count: deviceCount,
      max_devices,
      $setup,
      ...rest
    } = this.props;

    const name = planName;
    const icon = (
      <Box
        width={40}
        height={40}
        borderRadius="8px"
        bg="primary"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon icon={MdRadar} iconSize={24} fill="white" />
      </Box>
    );
    const infoTipIcon = <Icon icon={MdInfo} iconSize={12} style={{ verticalAlign: 'middle', marginBottom: 1 }} />;
    const externalLinkIcon = <Icon icon={MdOpenInNew} iconSize={12} color="primary" />;
    const warningIcon = <Icon iconSize={15} icon={MdWarning} color="primary" />;

    // Device usage calculations - using local calculations instead of global MobX
    const currentDeviceCount = deviceCount; // Current number of devices in use
    const deviceLimitReached = currentDeviceCount >= max_devices; // Check if at or over plan limit
    const fullLicense = max_devices >= 100; // Check if plan is full license (100+ devices)
    // Overage calculations - devices beyond plan limit
    const overagesValue = currentDeviceCount > max_devices ? currentDeviceCount - max_devices : 0;
    const overageLimitReached = overagesValue >= 100; // Critical threshold: 100+ devices over limit Critical threshold: 100+ devices over limit
    return (
      <Card borderRadius={8} p={2} {...rest}>
        <Flex flexWrap="wrap" justifyContent="space-between">
          <Flex flex="1 1 auto" mr={3}>
            <Box width={40} mr={2}>
              {icon}
            </Box>
            <Box maxWidth={700} minWidth={350}>
              <Text color="muted" as="div" mb="4px" fontWeight="600" fontSize="14px">
                NMS
              </Text>
              <Heading fontSize={18} level={4} mb={1}>
                {name}{' '}
              </Heading>
              <Text maxWidth={616} fontSize={12} color="muted" as="div" mb={1}>
                {planDescription}
              </Text>

              {currentDeviceCount === 0 && <Text color="muted">No devices have been added to this plan.</Text>}

              {currentDeviceCount > 0 && (
                <LightweightDataViewWrapper query={COMPANY_MPS_QUERY}>
                  {({ loading }) => {
                    // Progress bar calculations
                    const progressVal = max_devices > 0 ? currentDeviceCount / max_devices : 0; // Main progress (0-1)
                    const overagesProgressVal = Math.min(overagesValue / 100, 1.0); // Overage progress (0-1, capped at 100)
                    return (
                      <Flex mt="4px" maxWidth="100%" flexWrap={['wrap', 'wrap', 'nowrap']}>
                        {/* Main device usage progress bar */}
                        <div>
                          <Box minWidth={deviceLimitReached && fullLicense ? 415 : 615} mb={1}>
                            <ProgressBar
                              value={progressVal}
                              animate={loading}
                              stripes={loading}
                              intent={deviceLimitReached ? 'warning' : 'primary'}
                              barBgColor="none"
                            />
                          </Box>
                          <Flex justifyContent="space-between" alignItems="center">
                            {loading && <Text color="muted">Loading...</Text>}

                            {!loading && (
                              <Flex
                                width={deviceLimitReached ? 'auto' : '100%'}
                                flexDirection={deviceLimitReached ? 'column' : 'row'}
                                justifyContent={deviceLimitReached ? 'flex-start' : 'space-between'}
                                alignItems={deviceLimitReached ? 'flex-start' : 'center'}
                              >
                                <Text style={{ marginTop: 4 }} fontWeight="bold">
                                  Full-monitor Devices:
                                </Text>
                                <Text style={{ marginTop: 4 }} textAlign="left">
                                  {addCommas(currentDeviceCount >= max_devices ? max_devices : currentDeviceCount)} of{' '}
                                  {addCommas(max_devices)}
                                </Text>
                              </Flex>
                            )}
                          </Flex>
                        </div>
                        {/* overage progress bar - shown when device limit exceeded */}
                        {deviceLimitReached && fullLicense && (
                          <div style={{ marginLeft: 4 }}>
                            <Box width={200} mb={1}>
                              <ProgressBar
                                value={overagesProgressVal}
                                animate={loading}
                                stripes={loading}
                                intent="danger"
                                barBgColor="none"
                              />
                            </Box>
                            <Flex justifyContent="space-between" alignItems="center">
                              {loading ? (
                                <Text color="muted">Loading...</Text>
                              ) : (
                                <>
                                  <Flex flexDirection="column" alignItems="flex-start">
                                    <Text fontWeight="bold">Overage {infoTipIcon}:</Text>
                                    <Text style={{ marginTop: 4 }} textAlign="left">
                                      {addCommas(overagesValue)} of 100
                                    </Text>
                                  </Flex>
                                </>
                              )}
                            </Flex>
                          </div>
                        )}
                      </Flex>
                    );
                  }}
                </LightweightDataViewWrapper>
              )}
            </Box>
          </Flex>
          <Flex flexDirection="column">
            <Box width={425}>
              <Text color="muted" fontWeight="normal">
                Entitlements
              </Text>
              {/*  entitlements section */}
              <Flex mt={1} mb={1} flexDirection="column">
                {/* Full monitoring devices - premium monitoring with full feature set */}
                <Flex alignItems="center" justifyContent="space-between">
                  <Text fontWeight="bold">
                    Full-monitor Devices {infoTipIcon}:{' '}
                    <Text as="span" fontWeight="normal">
                      {addCommas(max_devices)}
                    </Text>
                  </Text>
                  <FilteredDevicesLink
                    style={{ marginLeft: 8 }}
                    filters={{ nmsConfigured: [true], isArchived: false }} // full monitoring devices that are configured
                  >
                    {externalLinkIcon}
                  </FilteredDevicesLink>
                </Flex>
                <Flex alignItems="center" justifyContent="space-between" mt={1}>
                  <Text fontWeight="bold">
                    ICMP-Only Devices {infoTipIcon}:{' '}
                    <Text as="span" fontWeight="normal">
                      {$setup.nmsIcmpOnlyDeviceCount} of Unlimited
                    </Text>
                  </Text>
                  <FilteredDevicesLink
                    style={{ marginLeft: 8 }}
                    filters={{ nmsConfigured: '', isArchived: false, icmpStatus: ['up'] }} // icmp only devices that are up
                  >
                    {externalLinkIcon}
                  </FilteredDevicesLink>
                </Flex>
              </Flex>

              <Text fontWeight="bold">
                Data Retention:{' '}
                <Text fontWeight="normal" as="span">
                  {full_retention} Days
                </Text>
              </Text>
            </Box>
            {/*  limit warning - full and nonfull licenses */}
            {deviceLimitReached && (
              <DeviceLimitWarning overageLimitReached={overageLimitReached} iconType={warningIcon} />
            )}
          </Flex>
        </Flex>
      </Card>
    );
  }
}

export default NmsDeviceUsageCard;
export { DeviceLimitWarning };
